import React, { useState } from 'react';
import { Container } from 'reactstrap';
import { Key, Mail } from 'react-feather';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { logo } from '../Data/Images/Home';
import { useLoginMutation } from '../features/auth/authApiSlice';
import { setCredentials } from '../features/auth/authSlice';
import { persistCustomerAuth } from '../utils/customerAuth';
import CustomLink from '../Components/CustomLink';
import { useTranslation } from 'react-i18next';

const Logins = () => {
  const { t } = useTranslation();
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const location = useLocation();
  const isComparisonLogin = location.state?.activeSubscriberRetake === true ||
    new URLSearchParams(location.search).get('redirect') === '/dashboard/comparison';
  const [email, setEmail] = useState(location.state?.prefilledEmail || '');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const history = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrMsg('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      toast.error(t('Auth_InvalidCredentialsToast'));
      return;
    }

    const expectedEmail = location.state?.activeSubscriberRetake
      ? location.state?.prefilledEmail?.trim().toLowerCase()
      : null;
    if (expectedEmail && trimmedEmail.toLowerCase() !== expectedEmail) {
      setErrMsg(t('Auth_InvalidCredentials'));
      return;
    }

    try {
      const userData = await login({
        email: trimmedEmail,
        password,
      }).unwrap();

      dispatch(setCredentials({ ...userData, storage: true }));
      persistCustomerAuth(userData);

      const params = new URLSearchParams(location.search);
      const requestedRedirect = params.get('redirect');
      const redirectTo = requestedRedirect?.startsWith('/') && !requestedRedirect.startsWith('//')
        ? requestedRedirect
        : '/dashboard';
      history(redirectTo);
      toast.success(t('Auth_LoginSuccess'));
    } catch (error) {
      if (error?.status === 401) {
        setErrMsg(t('Auth_InvalidCredentials'));
        return;
      }

      toast.error(error?.data?.message || t('Auth_GenericError'));
    }
  };

  return (
    <Container
      fluid={true}
      className="login-section !t-min-h-screen !t-py-0"
      style={{
        backgroundImage: `linear-gradient(var(--secondary-80),var(--secondary-80))`,
      }}
    >
      <div className="container t-flex t-min-h-screen t-flex-col t-items-center t-justify-center t-gap-8 t-px-4 t-py-10">
        <CustomLink to="/" className="t-block">
          <img src={logo} alt="Riise" width={120} />
        </CustomLink>

        <div className="t-w-full t-max-w-[420px] t-rounded-[20px] t-bg-surface t-px-6 t-py-8 t-shadow-2xl sm:t-px-10 sm:t-py-10">
          <div className="t-w-full">
            <header className="t-mb-7">
              <h1 className="t-m-0 t-font-serif t-text-[25px] t-font-normal t-leading-[1.08] t-text-navy sm:t-text-[26px]">
                {t('Auth_LoginTitle')}
              </h1>
              <p className="t-m-0 t-mt-2 t-text-sm t-leading-[1.45] t-text-teal-muted">
                {t(isComparisonLogin ? 'Auth_ComparisonLoginSubtitle' : 'Auth_LoginSubtitle')}
              </p>
            </header>

            <form className="t-flex t-flex-col t-gap-4" onSubmit={handleLogin}>
              <div>
                <label
                  className="t-mb-2 t-block t-text-xs t-font-extrabold t-uppercase t-leading-none t-tracking-wide t-text-navy"
                  htmlFor="email"
                >
                  {t('Email')}
                </label>
                <div className="t-flex t-min-h-[52px] t-items-center t-gap-3 t-rounded-xl t-border-2 t-border-g200 t-bg-white t-px-4 t-transition focus-within:t-border-quiz-teal focus-within:t-ring-4 focus-within:t-ring-teal-bg">
                  <Mail className="t-shrink-0 t-text-teal-muted" size={16} strokeWidth={1.8} aria-hidden="true" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="t-h-full t-min-w-0 t-flex-1 t-border-0 t-bg-transparent t-text-sm t-text-navy t-outline-none placeholder:t-text-g300"
                    placeholder={t('Auth_EmailPlaceholder')}
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                  />
                </div>
              </div>

              <div>
                <label
                  className="t-mb-2 t-block t-text-xs t-font-extrabold t-uppercase t-leading-none t-tracking-wide t-text-navy"
                  htmlFor="password"
                >
                  {t('Password')}
                </label>
                <div className="t-flex t-min-h-[52px] t-items-center t-gap-3 t-rounded-xl t-border-2 t-border-g200 t-bg-white t-px-4 t-transition focus-within:t-border-quiz-teal focus-within:t-ring-4 focus-within:t-ring-teal-bg">
                  <Key className="t-shrink-0 t-text-teal-muted" size={16} strokeWidth={1.8} aria-hidden="true" />
                  <input
                    id="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="t-h-full t-min-w-0 t-flex-1 t-border-0 t-bg-transparent t-text-sm t-text-navy t-outline-none placeholder:t-text-g300"
                    placeholder={t('Auth_PasswordPlaceholder')}
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                  />
                  <button
                    type="button"
                    className="t-shrink-0 t-border-0 t-bg-transparent t-p-0 t-text-xs t-font-extrabold t-text-quiz-teal"
                    onClick={() => setIsPasswordVisible((currentValue) => !currentValue)}
                  >
                    {isPasswordVisible ? t('Auth_HidePassword') : t('Auth_ShowPassword')}
                  </button>
                </div>
                {errMsg && <p className="t-m-0 t-mt-3 t-text-base t-font-semibold t-text-red-500">{errMsg}</p>}
              </div>

              <div className="t-flex t-justify-end">
                <CustomLink
                  to="/forgot-password"
                  className="t-border-0 t-border-b-2 t-border-solid t-border-quiz-teal t-text-[13px] t-font-extrabold !t-text-quiz-teal"
                >
                  {t('Auth_ForgotPasswordQuestion')}
                </CustomLink>
              </div>

              <button
                type="submit"
                className="t-mt-2 t-min-h-[56px] t-w-full t-rounded-xl t-border-0 t-bg-butter t-text-[15px] t-font-extrabold t-text-navy t-transition hover:-t-translate-y-0.5 hover:t-shadow-lg disabled:t-cursor-not-allowed disabled:t-opacity-70"
                disabled={isLoading}
              >
                {isLoading ? t('Auth_LoginLoading') : t('Auth_LoginCta')}
              </button>
            </form>

            <div className="t-my-6 t-flex t-items-center t-gap-4 t-text-xs t-uppercase t-text-teal-muted">
              <span className="t-h-px t-flex-1 t-bg-g200" />
              <span>{t('Auth_Or')}</span>
              <span className="t-h-px t-flex-1 t-bg-g200" />
            </div>

            <CustomLink
              to="/magic-login"
              className="t-flex t-min-h-[52px] t-w-full t-items-center t-justify-center t-rounded-xl t-border-2 t-border-solid t-border-g200 t-bg-white t-px-5 t-text-center t-text-sm t-font-extrabold !t-text-navy t-transition hover:t-border-quiz-teal"
            >
              {t('Auth_MagicLogin_Link')}
            </CustomLink>

            <div className="t-mt-7 t-border-0 t-border-t t-border-solid t-border-g200 t-pt-5 t-text-[13px] t-leading-tight t-text-navy">
              <span>{t('Auth_NewHere')} </span>
              <CustomLink to="/quiz" className="t-font-extrabold !t-text-quiz-teal">
                {t('Auth_CreateAccount')}
              </CustomLink>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Logins;
